/* eslint-disable react/jsx-props-no-spreading */
import { ConfigProvider } from 'antd';
import UserWizard from 'components/UserWizard';
import React, { memo, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import { withBaseLayout } from 'routes/BaseLayout';
import PageNotFound from 'routes/Errors/PageNotFound';
import TokenExpired from 'routes/Errors/TokenExpired';
import ResetPassword from 'routes/ResetPassword';
import ROUTES from 'routes/routeConstants';
import { PrivateRoute, PublicRoute } from 'routes/routing';
import SignIn from 'routes/SignIn';
import SignUp from 'routes/SignUp';
import URLSearchParams from 'url-search-params';

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_SEMI_DARK,
} from '../../constants/ThemeSetting';
import AppLocale from '../../lngProvider';
import AuthAction from '../../redux/auth/auth.actions';
import SettingsAction from '../../redux/settings/settings.actions';
import { RootState } from '../../redux/types';
import MainApp from './MainApp';

const { setInitUrl } = AuthAction;
const { onLayoutTypeChange, onNavStyleChange, setThemeType } = SettingsAction;

const App = () => {
  const dispatch = useDispatch();
  const { locale, navStyle, layoutType, themeType } = useSelector<RootState, RootState['settings']>(
    ({ settings }) => settings,
  );
  const { initURL } = useSelector<RootState, RootState['auth']>(({ auth }) => auth);
  const location = useLocation();

  const setLayoutType = (pLayoutType: string) => {
    if (pLayoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('full-layout');
    } else if (pLayoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove('full-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('boxed-layout');
    } else if (pLayoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('full-layout');
      document.body.classList.add('framed-layout');
    }
  };

  const setNavStyle = (pNavStyle: string) => {
    if (
      pNavStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
      pNavStyle === NAV_STYLE_DARK_HORIZONTAL ||
      pNavStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      pNavStyle === NAV_STYLE_ABOVE_HEADER ||
      pNavStyle === NAV_STYLE_BELOW_HEADER
    ) {
      document.body.classList.add('full-scroll');
      document.body.classList.add('horizontal-layout');
    } else {
      document.body.classList.remove('full-scroll');
      document.body.classList.remove('horizontal-layout');
    }
  };

  const setThemeTypeUI = (pThemeType: string) => {
    if (pThemeType === THEME_TYPE_SEMI_DARK) {
      document.body.classList.remove('dark-theme');
    } else {
      document.body.classList.add('dark-theme');
    }
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.type = 'text/css';
    link.rel = 'stylesheet';
    link.href = '/css/style.css';
    link.className = 'gx-style';
    document.body.appendChild(link);
  }, []);

  useEffect(() => {
    if (initURL === '') {
      dispatch(setInitUrl(location.pathname));
    }
    const params = new URLSearchParams(location.search);

    if (params.has('theme')) {
      dispatch(setThemeType(params.get('theme')));
    }
    if (params.has('nav-style')) {
      dispatch(onNavStyleChange(params.get('nav-style')));
    }
    if (params.has('layout-type')) {
      dispatch(onLayoutTypeChange(params.get('layout-type')));
    }
    setLayoutType(layoutType);
    setThemeTypeUI(themeType);
    setNavStyle(navStyle);
  });

  // useEffect(() => {
  //   if (email && isTokenValid) {
  //     console.log('OUTPUT ~ file: index.tsx ~ line 120 ~ useEffect ~ email', email);
  //     dispatch(
  //       UserAction.fetchUserStart({
  //         email,
  //       }),
  //     );
  //   }
  // }, [email, isTokenValid]);

  const currentAppLocale = AppLocale[locale.locale];

  return (
    <ConfigProvider locale={currentAppLocale.antd}>
      <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
        <Switch>
          <PublicRoute location={location} exact path={ROUTES.SIGN_IN} component={SignIn} />
          <PublicRoute location={location} exact path={ROUTES.SIGN_UP} component={SignUp} />
          <PublicRoute location={location} exact path={ROUTES.RESET_PASSWORD} component={ResetPassword} />
          <Route location={location} exact path={ROUTES.PAGE_NOT_FOUND} component={withBaseLayout(PageNotFound)} />
          <Route location={location} exact path={ROUTES.TOKEN_EXPIRED} component={withBaseLayout(TokenExpired)} />
          <PrivateRoute location={location} exact path={ROUTES.USER_WIZARD} component={UserWizard} />
          <Route path='/' location={location} component={MainApp} />
        </Switch>
      </IntlProvider>
    </ConfigProvider>
  );
};

export default memo(App);
